/**
 * Release Hub — Localized field resolver
 *
 * rPI ERP stores test feature metadata (names, steps, expected results) as
 * parallel keyed fields like `nameHu`, `nameEn`, `nameDe`, `nameUk`. This
 * helper resolves the right one for the current locale with a guaranteed
 * English fallback, without violating strict TypeScript rules:
 *
 * - No `as Record<string, string>` casts (which would bypass
 *   `noUncheckedIndexedAccess`).
 * - `fallbackEn` is required by the type system — callers cannot forget it.
 * - Missing language fields gracefully fall through to the English default,
 *   so adding a new language doesn't crash pre-existing data.
 *
 * @param fields      Object with optional per-language fields
 * @param locale      App locale from `useTranslation().locale`
 * @param fallbackEn  The English field value (always required as last resort)
 */
export type AppLocale = 'hu' | 'en' | 'de' | 'uk';

export function normalizeLocale(locale: string): AppLocale {
  return locale === 'hu' || locale === 'de' || locale === 'uk' ? locale : 'en';
}

/**
 * Resolves a value from a multi-language fields object.
 * Prefers the current locale, falls back to English.
 */
export function getLocalizedField(
  fields: { hu?: string; en: string; de?: string; uk?: string },
  locale: string
): string {
  const normalized = normalizeLocale(locale);
  if (normalized === 'hu') return fields.hu ?? fields.en;
  if (normalized === 'de') return fields.de ?? fields.en;
  if (normalized === 'uk') return fields.uk ?? fields.en;
  return fields.en;
}

/**
 * Resolves a localized feature name from `{nameHu, nameEn, nameDe, nameUk}`.
 * Used by TestFeatureCard + search/filter logic.
 */
export function getLocalizedFeatureName(
  feature: { nameHu: string; nameEn: string; nameDe?: string; nameUk?: string },
  locale: string
): string {
  const normalized = normalizeLocale(locale);
  if (normalized === 'hu') return feature.nameHu;
  if (normalized === 'de') return feature.nameDe ?? feature.nameEn;
  if (normalized === 'uk') return feature.nameUk ?? feature.nameEn;
  return feature.nameEn;
}
